import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { roleLabel, type AdminPerson, type RoleRow } from '../data/people.ts'
import { accessLink, postAdmin } from '../lib/adminApi.ts'
import { publicOrigin } from '../platform/origin.ts'
import { QrSvg } from '../ui/QrSvg.tsx'
import { Banner, Field, Pill } from '../ui/components.tsx'
import { type Sensitive } from '../ui/sensitive.tsx'
import { NoFactorBanner } from './people/Factor.tsx'

/** The administrator's panel for one person: roles, the login (and the one-time link to set a password, and "sign out all their devices"), second factors.
 *  Every change is a Y* command (online, behind a fresh code); the auth module's part goes through the Node admin endpoint. */
export function PersonAccess({ person, roles, s, onChanged }: { person: AdminPerson; roles: Array<{ code: string; label: { fr: string; en: string } }>; s: Sensitive; onChanged: () => void }) {
  const { t, i18n } = useTranslation()
  const { org } = useClub()
  const [role, setRole] = useState('')
  const [email, setEmail] = useState('')
  const [link, setLink] = useState<string | null>(null)
  // M23 item 43: "Révoquer" asks twice, like every other destructive act on the page (the membership whose first tap was given)
  const [revoking, setRevoking] = useState<string | null>(null)
  const membershipId = useRef(newId())
  const authId = useRef(newId())
  const grantable = roles.filter((r) => r.code !== 'kiosk' && !person.memberships.some((m) => m.role_code === r.code))
  const done = (okKey: string, then?: () => Promise<void>) => async (applied: boolean | undefined) => { if (applied) { if (then) await then(); onChanged() } void okKey }

  async function makeLink(kind: 'invite' | 'recover') {
    void kind
    const out = await s.wrap(() => postAdmin<{ token_hash: string; email: string }>(`/admin/people/${person.person_id}/access-link`, { org_id: org.org_id, email }))
    if (out) { setLink(accessLink(publicOrigin() ?? '', 'accept', out.token_hash, out.email)); s.setOk(t('people.linkReady')) }
  }

  return (
    <div className="flex flex-col gap-4 border-t pt-4" data-testid="person-access">
      {/* M23 item 32: every change here needs the administrator's own second factor; said before the forms when it is missing */}
      <NoFactorBanner testId="access-no-factor" />
      <h3 className="text-lg font-bold">{t('people.rolesTitle')}</h3>
      <ul className="flex flex-col gap-2">
        {person.memberships.length === 0 && <li className="opacity-70">{t('people.noRole')}</li>}
        {person.memberships.map((m) => {
          const label = roleLabel(roles as RoleRow[], m.role_code, i18n.language)
          return (
            <li key={m.membership_id} className="flex flex-wrap items-center justify-between gap-2" data-testid="membership-row">
              <Pill tone="green">{label}</Pill>
              {revoking === m.membership_id ? (
                <span className="flex flex-wrap gap-2">
                  <button className="btn btn-danger !px-4" disabled={s.busy || !s.online} data-testid="role-revoke-confirm"
                    onClick={() => void s.command('member.revoke_role@1', { membership_id: m.membership_id }, 'people.roleRevoked').then((ok) => { setRevoking(null); return done('revoked')(ok) })}>{t('people.revokeConfirm', { role: label })}</button>
                  <button className="btn !px-4" onClick={() => setRevoking(null)} data-testid="role-revoke-cancel">{t('common.cancel')}</button>
                </span>
              ) : (
                <button className="btn !min-h-11 !px-4" disabled={s.busy || !s.online} onClick={() => setRevoking(m.membership_id)} data-testid="role-revoke">{t('people.revoke')}</button>
              )}
            </li>
          )
        })}
      </ul>
      {grantable.length > 0 && (
        <div className="flex items-end gap-2">
          <Field label={t('people.grant')} htmlFor={`gr-${person.person_id}`}>
            <select id={`gr-${person.person_id}`} className="input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="role-pick">
              <option value="">{t('mounts.choose')}</option>
              {grantable.map((r) => <option key={r.code} value={r.code}>{roleLabel(roles as RoleRow[], r.code, i18n.language)}</option>)}
            </select>
          </Field>
          <button className="btn btn-primary" disabled={!role || s.busy || !s.online} onClick={() => void s.command('member.grant_role@1', { membership_id: membershipId.current, person_id: person.person_id, role_code: role }, 'people.roleGranted').then(async (ok) => { if (ok) { membershipId.current = newId(); setRole('') } await done('granted')(ok) })} data-testid="role-grant">{t('people.grantGo')}</button>
        </div>
      )}

      <h3 className="text-lg font-bold">{t('people.loginTitle')}</h3>
      <p className="meta">{person.has_login ? t('people.loginHas') : t('people.loginNone')}</p>
      <Field label={t('auth.email')} htmlFor={`em-${person.person_id}`}>
        <input id={`em-${person.person_id}`} className="input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
      </Field>
      <button className="btn" disabled={s.busy || !s.online || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)} data-testid="login-go" onClick={async () => {
        if (!person.has_login) {
          const ok = await s.command('person.link_login@1', { person_id: person.person_id, auth_user_id: authId.current }, 'people.loginLinked')
          if (!ok) return
          authId.current = newId(); onChanged()
        }
        await makeLink('invite')
      }}>{person.has_login ? t('people.newLink') : t('people.createLogin')}</button>
      {person.has_login && (
        // M22: a lost phone of a person with no factor (docs/runbooks/lost-device.md): every session of the login ends, behind the page's second-factor field
        <button className="btn" disabled={s.busy || !s.online} data-testid="sign-out-all"
          onClick={() => void s.wrap(() => postAdmin<{ revoked: number }>(`/admin/people/${person.person_id}/sign-out`, { org_id: org.org_id })).then((r) => { if (r) s.setOk(t('people.signedOutAll', { count: r.revoked ?? 0 })) })}>{t('people.signOutAll')}</button>
      )}
      {link && (
        <div className="card flex flex-col items-center gap-3" data-testid="access-link">
          <p className="text-sm">{t('people.linkHelp')}</p>
          <QrSvg text={link} className="size-48 bg-white" title={t('people.linkQr')} />
          <input className="input text-xs" readOnly value={link} onFocus={(e) => e.currentTarget.select()} data-testid="access-link-text" aria-label={t('people.linkTitle')} />
        </div>
      )}

      <h3 className="text-lg font-bold">{t('people.factorsTitle')}</h3>
      <p className="meta">{t('people.factorsHelp')}</p>
      <ul className="flex flex-col gap-2">
        {person.factors.length === 0 && <li className="opacity-70">{t('people.noFactor')}</li>}
        {person.factors.map((f) => (
          <li key={f.factor_id} className="card flex flex-wrap items-center justify-between gap-2" data-testid="factor-row">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{f.name ?? t('people.factor')}</span>
              <Pill tone={f.revoked ? 'red' : f.approved ? 'green' : 'amber'}>{f.revoked ? t('people.factorRevoked') : f.approved ? t('people.factorApproved') : t('people.factorWaiting')}</Pill>
              {f.status !== 'verified' && <Pill tone="gray">{t('people.factorUnverified')}</Pill>}
            </span>
            <span className="flex gap-2">
              {!f.approved && !f.revoked && f.status === 'verified' && (
                <button className="btn btn-primary !px-4" disabled={s.busy || !s.online} data-testid="factor-approve"
                  onClick={() => void s.command('mfa.approve_factor@1', { approval_id: newId(), person_id: person.person_id, factor_id: f.factor_id }, 'people.factorNowApproved').then(done('approved'))}>{t('people.approve')}</button>
              )}
              {f.approved && (
                <button className="btn !min-h-11 !px-4" disabled={s.busy || !s.online} data-testid="factor-revoke" onClick={() => void s.command('mfa.revoke_factor@1', { person_id: person.person_id, factor_id: f.factor_id }, 'people.factorNowRevoked').then(done('revoked'))}>{t('people.revoke')}</button>
              )}
              {f.revoked && (
                <button className="btn !min-h-11 !px-4" disabled={s.busy || !s.online} data-testid="factor-delete"
                  onClick={() => void s.wrap(() => postAdmin(`/admin/people/${person.person_id}/factors/${f.factor_id}/delete`, { org_id: org.org_id })).then((r) => { if (r) { s.setOk(t('people.factorDeleted')); onChanged() } })}>{t('people.factorDelete')}</button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The one-time code a person needs for a first PIN, or after forgetting it (head greenkeeper or administrator). Shown once; stored hashed. */
export function ResetCodeBlock({ personId, s }: { personId: string; s: Sensitive }) {
  const { t } = useTranslation()
  const { org, deviceId } = useClub()
  const [code, setCode] = useState<{ code: string; expires_at: string } | null>(null)
  return (
    <div className="flex flex-col gap-3" data-testid="reset-block">
      <h3 className="text-lg font-bold">{t('people.pinTitle')}</h3>
      <p className="meta">{t('people.pinHelp')}</p>
      <button className="btn" disabled={s.busy || !s.online} data-testid="reset-code-go"
        onClick={() => void s.call<{ code: string; expires_at: string }>('kiosk_issue_reset_code', { p_org: org.org_id, p_person: personId, p_device_id: deviceId }).then((r) => { if (r) setCode(r) })}>{t('people.pinIssue')}</button>
      {code && (
        <Banner tone="ok"><span className="block text-center text-3xl font-bold tracking-widest" data-testid="reset-code">{code.code}</span><span className="block text-center text-xs">{t('people.pinOnce')}</span></Banner>
      )}
    </div>
  )
}
